import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, Buildings, CalendarBlank } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    axios.get(`${API}/blog/${slug}`)
      .then(r => {
        setPost(r.data);
        // Per-article SEO meta
        document.title = `${r.data.title} — HostCockpit`;
        const setMeta = (name, content, property = false) => {
          const attr = property ? 'property' : 'name';
          let el = document.querySelector(`meta[${attr}="${name}"]`);
          if (!el) {
            el = document.createElement('meta');
            el.setAttribute(attr, name);
            document.head.appendChild(el);
          }
          el.setAttribute('content', content);
        };
        setMeta('description', r.data.meta_description || r.data.excerpt || '');
        setMeta('keywords', r.data.keywords || '');
        setMeta('og:title', r.data.title, true);
        setMeta('og:description', r.data.meta_description || r.data.excerpt || '', true);
        setMeta('og:type', 'article', true);
        setMeta('og:url', `https://hostcockpit.com/blog/${r.data.slug}`, true);
        // Canonical
        let canon = document.querySelector('link[rel="canonical"]');
        if (!canon) { canon = document.createElement('link'); canon.setAttribute('rel', 'canonical'); document.head.appendChild(canon); }
        canon.setAttribute('href', `https://hostcockpit.com/blog/${r.data.slug}`);
        // Article JSON-LD structured data
        const oldLd = document.getElementById('blog-article-ld');
        if (oldLd) oldLd.remove();
        const ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.id = 'blog-article-ld';
        ld.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: r.data.title,
          description: r.data.meta_description || r.data.excerpt,
          datePublished: r.data.published_at,
          author: { '@type': 'Organization', name: 'HostCockpit' },
          publisher: { '@type': 'Organization', name: 'HostCockpit', url: 'https://hostcockpit.com/' },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `https://hostcockpit.com/blog/${r.data.slug}` },
        });
        document.head.appendChild(ld);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <p className="p-10 text-sm text-zinc-500">Loading...</p>;
  if (notFound) return (
    <div className="max-w-2xl mx-auto py-20 px-6 text-center">
      <h1 className="text-2xl font-bold">Article not found</h1>
      <Link to="/blog" className="text-[#0052FF] underline mt-4 inline-block">← Back to blog</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <header className="border-b border-zinc-200 sticky top-0 bg-white z-40">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#0052FF] rounded-sm flex items-center justify-center">
              <Buildings weight="bold" className="text-white" size={14} />
            </div>
            <span className="text-lg font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>HostCockpit</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/blog" className="text-sm text-zinc-600 hover:text-zinc-900 hidden sm:inline">Blog</Link>
            <Link to="/register" className="px-4 py-2 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD]">Start Free Trial</Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/blog" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-8" data-testid="back-to-blog">
          <ArrowLeft size={12} /> Back to all articles
        </Link>

        <article>
          <div className="flex items-center gap-3 mb-4 text-xs text-zinc-500">
            <CalendarBlank size={12} />
            <time>{post.published_at?.slice(0, 10)}</time>
            <span>·</span>
            <span>{post.author}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-zinc-950 tracking-tight mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            {post.title}
          </h1>

          <div
            className="prose prose-lg prose-zinc max-w-none
              prose-headings:font-black prose-headings:tracking-tight prose-headings:text-zinc-950
              prose-h1:hidden
              prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-5 prose-h2:border-l-4 prose-h2:border-[#0052FF] prose-h2:pl-4 prose-h2:leading-tight
              prose-h3:text-xl prose-h3:mt-10 prose-h3:mb-3 prose-h3:text-zinc-900
              prose-p:text-[17px] prose-p:text-zinc-700 prose-p:leading-[1.8] prose-p:my-5
              prose-a:text-[#0052FF] prose-a:font-semibold prose-a:no-underline hover:prose-a:underline
              prose-strong:text-zinc-950 prose-strong:font-bold
              prose-ul:my-6 prose-ul:space-y-2 prose-li:my-0 prose-li:text-[17px] prose-li:text-zinc-700 prose-li:leading-[1.8] prose-li:marker:text-[#0052FF]
              prose-ol:my-6 prose-ol:space-y-2
              prose-code:bg-zinc-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:text-[#0052FF] prose-code:before:content-[''] prose-code:after:content-['']
              prose-blockquote:border-l-4 prose-blockquote:border-[#0052FF] prose-blockquote:bg-blue-50/40 prose-blockquote:py-3 prose-blockquote:px-5 prose-blockquote:not-italic prose-blockquote:text-zinc-800 prose-blockquote:font-medium prose-blockquote:my-8
              prose-hr:my-12 prose-hr:border-zinc-200
              prose-img:rounded-sm prose-img:my-8
              prose-table:my-8 prose-th:bg-zinc-50 prose-th:font-bold prose-th:text-zinc-900 prose-th:text-sm prose-td:text-sm prose-td:text-zinc-700"
            data-testid="blog-content"
          >
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </div>
        </article>

        <div className="mt-16 p-8 border border-zinc-200 bg-gradient-to-br from-blue-50 to-white text-center">
          <h3 className="text-2xl font-bold text-zinc-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Ready to run your Airbnb without a manager?</h3>
          <p className="text-sm text-zinc-600 mb-5">14-day free trial. No credit card required.</p>
          <Link to="/register" className="inline-block px-6 py-3 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD]">Start Free Trial</Link>
        </div>
      </main>

      <footer className="border-t border-zinc-200 py-8 mt-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between text-xs text-zinc-500 gap-2">
          <p>© 2026 HostCockpit. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/" className="hover:text-zinc-900">Home</Link>
            <Link to="/blog" className="hover:text-zinc-900">Blog</Link>
            <Link to="/sms-consent" className="hover:text-zinc-900">SMS Terms</Link>
            <a href="mailto:hosts@hostcockpit.com" className="hover:text-zinc-900">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
